import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import { lazy, Suspense, useEffect } from 'react';
import Layout from '@/components/Layout';
import ErrorBoundary from '@/components/ErrorBoundary';
import VersionCheckBanner from '@/components/VersionCheckBanner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ConfirmProvider } from '@/components/ConfirmDialog';
import { IS_APP, IS_MARKETING } from '@/lib/siteMode';
import { RedirectToApp, RedirectToMarketing } from '@/lib/CrossSiteRedirect';

// Lazy-loaded pages — each becomes its own JS chunk, loaded on demand
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Clients = lazy(() => import('@/pages/Clients'));
const Sites = lazy(() => import('@/pages/Sites'));
const Stock = lazy(() => import('@/pages/Stock'));
const Suppliers = lazy(() => import('@/pages/Suppliers'));
const Orders = lazy(() => import('@/pages/Orders'));
const Backorders = lazy(() => import('@/pages/Backorders'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const Quotes = lazy(() => import('@/pages/Quotes'));
const Settings = lazy(() => import('@/pages/Settings'));
const LearningSystem = lazy(() => import('@/pages/LearningSystem'));
const Fleet = lazy(() => import('@/pages/Fleet'));
const VehicleChecks = lazy(() => import('@/pages/VehicleChecks'));
const InvoiceLeaks = lazy(() => import('@/pages/InvoiceLeaks'));
const AutoEnrolmentForecast = lazy(() => import('@/pages/AutoEnrolmentForecast'));
const Assets = lazy(() => import('@/pages/Assets'));
const StaffNew = lazy(() => import('@/pages/StaffNew'));
const Payroll = lazy(() => import('@/pages/Payroll'));
const PayMigration = lazy(() => import('@/pages/PayMigration'));
const Profile = lazy(() => import('@/pages/Profile'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const AdminPortal = lazy(() => import('@/pages/AdminPortal'));
const AdminLogin = lazy(() => import('@/pages/AdminLogin'));
const MandateCompletePage = lazy(() => import('@/pages/MandateCompletePage'));
const DriverApp = lazy(() => import('@/pages/DriverApp'));
const ClientLogin = lazy(() => import('@/pages/ClientLogin'));
const ClientPortal = lazy(() => import('@/pages/ClientPortal'));
const ClientHours = lazy(() => import('@/pages/ClientHours'));
const ClientStaff = lazy(() => import('@/pages/ClientStaff'));
const ClientInvoices = lazy(() => import('@/pages/ClientInvoices'));
const ClientAudits = lazy(() => import('@/pages/ClientAudits'));
const ClientDocuments = lazy(() => import('@/pages/ClientDocuments'));
const Audits = lazy(() => import('@/pages/Audits'));
const Documents = lazy(() => import('@/pages/Documents'));
const AuditLogin = lazy(() => import('@/pages/AuditLogin'));
const AuditDashboard = lazy(() => import('@/pages/AuditDashboard'));
const AuditForm = lazy(() => import('@/pages/AuditForm'));
const SickPayAlerts = lazy(() => import('@/pages/SickPayAlerts'));
const DataImport = lazy(() => import('@/pages/DataImport'));
const FixImportCompanyId = lazy(() => import('@/pages/FixImportCompanyId'));
const Attendance = lazy(() => import('@/pages/Attendance'));
const HRPortal = lazy(() => import('@/pages/HRPortal'));
const StockReports = lazy(() => import('@/pages/StockReports'));
const Depots = lazy(() => import('@/pages/Depots'));
const ELearningLogin = lazy(() => import('@/pages/ELearningLogin'));
const ELearningDashboard = lazy(() => import('@/pages/ELearningDashboard'));
const ELearningCourse = lazy(() => import('@/pages/ELearningCourse'));
const ELearningCertificates = lazy(() => import('@/pages/ELearningCertificates'));
const ELearningAdmin = lazy(() => import('@/pages/ELearningAdmin'));
const ELearningLayout = lazy(() => import('@/components/ELearningLayout').then(m => ({ default: m.ELearningLayout })));
const ManagerLogin = lazy(() => import('@/pages/manager/ManagerLogin'));
const ManagerLayout = lazy(() => import('@/pages/manager/ManagerLayout'));
const ManagerDashboard = lazy(() => import('@/pages/manager/ManagerDashboard'));
const ManagerSites = lazy(() => import('@/pages/manager/ManagerSites'));
const ManagerStaff = lazy(() => import('@/pages/manager/ManagerStaff'));
const ManagerOrders = lazy(() => import('@/pages/manager/ManagerOrders'));
const ManagerOnboarding = lazy(() => import('@/pages/manager/ManagerOnboarding'));
const ManagerAudits = lazy(() => import('@/pages/manager/ManagerAudits'));
const ManagerAuditDetail = lazy(() => import('@/pages/manager/ManagerAuditDetail'));
const PATScanner = lazy(() => import('@/pages/PATScanner'));
const WarehouseApp = lazy(() => import('@/pages/WarehouseApp'));

// Marketing site — public routes with their own layout
const MarketingHome = lazy(() => import('@/marketing/pages/Home'));
const MarketingFeatures = lazy(() => import('@/marketing/pages/Features'));
const MarketingPricing = lazy(() => import('@/marketing/pages/Pricing'));
const MarketingAbout = lazy(() => import('@/marketing/pages/About'));
const MarketingContact = lazy(() => import('@/marketing/pages/Contact'));
const MarketingRoadmap = lazy(() => import('@/marketing/pages/Roadmap'));
const MarketingShop = lazy(() => import('@/marketing/pages/Shop'));
const MarketingShopCart = lazy(() => import('@/marketing/pages/ShopCart'));
const MarketingShopSuccess = lazy(() => import('@/marketing/pages/ShopCheckoutSuccess'));
const MarketingShopCancel = lazy(() => import('@/marketing/pages/ShopCheckoutCancel'));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
    </div>
  </div>
);

const PrivateRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    // Route logged-out /admin traffic to the branded admin login page so the
    // user knows they're signing into the platform-operator side, not a tenant.
    const isAdminPath = location.pathname.startsWith('/admin');
    return <Navigate to={isAdminPath ? '/admin/login' : '/login'} replace />;
  }

  if (adminOnly) {
    const isSuperadmin = user.role === 'super_admin' || user.role === 'superadmin';
    if (!isSuperadmin) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

// Root route: authenticated staff see their dashboard, everyone else lands on
// the marketing homepage. In production, site-mode overrides this — the app
// deployment always sends "/" through the auth flow, and the marketing
// deployment always shows the marketing homepage.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (IS_MARKETING) return <MarketingHome />;
  if (IS_APP) {
    if (loading) return <LoadingSpinner />;
    return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
  }
  if (loading) return <LoadingSpinner />;
  return user ? <Navigate to="/dashboard" replace /> : <MarketingHome />;
};

function App() {
  // Cleanup any stale root-scoped service worker that's intercepting all API calls
  // (left behind from the original Driver App PWA registration). Safe no-op if none.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const path = window.location.pathname;
    if (path.startsWith('/driver')) return; // Driver app handles its own SW lifecycle

    // Strip the cross-domain redirect marker once a build owns the route,
    // so it never leaks into page-level query param parsing.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('xdr')) {
        url.searchParams.delete('xdr');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (e) { /* no-op */ }

    let didUnregister = false;
    navigator.serviceWorker.getRegistrations().then(async (regs) => {
      for (const reg of regs) {
        const scope = reg.scope || '';
        if (!scope.endsWith('/driver/')) {
          await reg.unregister();
          didUnregister = true;
          console.log('[App] Unregistered stale SW with scope:', scope);
        }
      }

      // Also nuke any old caches the SW left behind
      if (window.caches) {
        const names = await caches.keys();
        for (const name of names) {
          if (name.startsWith('cleanbiz-driver') || name.includes('offline')) {
            await caches.delete(name);
          }
        }
      }

      // If we cleaned anything up, force a single reload so the page loads without the SW intercept.
      // Use sessionStorage to make sure we only reload once per session.
      if (didUnregister && !sessionStorage.getItem('sw_cleanup_done')) {
        sessionStorage.setItem('sw_cleanup_done', '1');
        window.location.reload();
      }
    }).catch(() => {});
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <ConfirmProvider>
          <BrowserRouter>
        <div className="App">
          <VersionCheckBanner />
          <Suspense fallback={<LoadingSpinner />}>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<RootRoute />} />

              {/* ============ MARKETING SITE ============ */}
              {!IS_APP && (
                <>
                  <Route path="/features" element={<MarketingFeatures />} />
                  <Route path="/pricing" element={<MarketingPricing />} />
                  <Route path="/about" element={<MarketingAbout />} />
                  <Route path="/contact" element={<MarketingContact />} />
                  <Route path="/roadmap" element={<MarketingRoadmap />} />
                  <Route path="/shop" element={<MarketingShop />} />
                  <Route path="/shop/cart" element={<MarketingShopCart />} />
                  <Route path="/shop/checkout/success" element={<MarketingShopSuccess />} />
                  <Route path="/shop/checkout/cancel" element={<MarketingShopCancel />} />
                </>
              )}

              {/* ============ APP / AUTH / PORTALS ============ */}
              {!IS_MARKETING && (
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin/login" element={<AdminLogin />} />

                  {/* Client Portal Routes (Separate from office system) */}
                  <Route path="/client-portal" element={<ClientLogin />} />
                  <Route path="/client-portal/login" element={<ClientLogin />} />
                  <Route path="/client-portal/dashboard" element={<ClientPortal />} />
                  <Route path="/client-portal/hours" element={<ClientHours />} />
                  <Route path="/client-portal/staff" element={<ClientStaff />} />
                  <Route path="/client-portal/invoices" element={<ClientInvoices />} />
                  <Route path="/client-portal/audits" element={<ClientAudits />} />
                  <Route path="/client-portal/documents" element={<ClientDocuments />} />

                  {/* Area Manager Audit App Routes (Separate mobile app) */}
                  <Route path="/audits/login" element={<AuditLogin />} />
                  <Route path="/audits/dashboard" element={<AuditDashboard />} />
                  <Route path="/audits/new" element={<AuditForm />} />

                  {/* Public driver app - no auth required */}
                  <Route path="/driver" element={<DriverApp />} />

                  {/* PWA PAT Scanner - public route for field engineers */}
                  <Route path="/pat-scanner" element={<PATScanner />} />

                  {/* Warehouse App - unified picking + PAT scanning with PIN login */}
                  <Route path="/warehouse" element={<WarehouseApp />} />

                  {/* E-Learning Portal Routes */}
                  <Route path="/elearning" element={<ELearningLogin />} />
                  <Route path="/elearning" element={<ELearningLayout />}>
                    <Route path="dashboard" element={<ELearningDashboard />} />
                    <Route path="course/:courseId" element={<ELearningCourse />} />
                    <Route path="certificates" element={<ELearningCertificates />} />
                  </Route>

                  {/* FaciliTrack Manager Hub Routes */}
                  <Route path="/manager/login" element={<ManagerLogin />} />
                  <Route path="/manager" element={<ManagerLayout />}>
                    <Route path="dashboard" element={<ManagerDashboard />} />
                    <Route path="sites" element={<ManagerSites />} />
                    <Route path="staff" element={<ManagerStaff />} />
                    <Route path="orders" element={<ManagerOrders />} />
                    <Route path="onboarding" element={<ManagerOnboarding />} />
                    <Route path="audits" element={<ManagerAudits />} />
                    <Route path="audits/:auditId" element={<ManagerAuditDetail />} />
                  </Route>

                  {/* Data Import Tool - Standalone page, no auth required */}
                  <Route path="/data-import" element={<DataImport />} />
                  {/* Forgiving alias — people naturally type it both ways */}
                  <Route path="/import-data" element={<Navigate to="/data-import" replace />} />

                  {/* Fix Import Company ID - Admin tool */}
                  <Route path="/fix-import-company-id" element={<FixImportCompanyId />} />

                  <Route
                    path="/dashboard"
                    element={
                      <PrivateRoute>
                        <Layout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                  </Route>

                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <Layout />
                      </PrivateRoute>
                    }
                  >
                    <Route path="clients" element={<Clients />} />
                    <Route path="sites" element={<Sites />} />
                    <Route path="stock" element={<Stock />} />
                    <Route path="stock-reports" element={<StockReports />} />
                    <Route path="depots" element={<Depots />} />
                    <Route path="suppliers" element={<Suppliers />} />
                    <Route path="orders" element={<Orders />} />
                    <Route path="backorders" element={<Backorders />} />
                    <Route path="invoices" element={<Invoices />} />
                    <Route path="quotes" element={<Quotes />} />
                    <Route path="fleet" element={<Fleet />} />
                    <Route path="vehicle-checks" element={<VehicleChecks />} />
                    <Route path="invoice-leaks" element={<InvoiceLeaks />} />
                    <Route path="auto-enrolment-forecast" element={<AutoEnrolmentForecast />} />
                    <Route path="assets" element={<Assets />} />
                    <Route path="staff" element={<StaffNew />} />
                    <Route path="attendance" element={<Attendance />} />
                    <Route path="payroll" element={<Payroll />} />
                    <Route path="pay-migration" element={<PayMigration />} />
                    <Route path="sick-pay-alerts" element={<SickPayAlerts />} />
                    <Route path="hr" element={<HRPortal />} />
                    <Route path="audits" element={<Audits />} />
                    <Route path="elearning-admin" element={<ELearningAdmin />} />
                    <Route path="documents" element={<Documents />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="learning-system" element={<LearningSystem />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="admin" element={<AdminPortal />} />
                    <Route path="admin/billing/mandate-complete" element={<MandateCompletePage />} />
                  </Route>
                </>
              )}

              {/* Catch-all: cross-domain redirect when a build doesn't own
                  the route, else back to root in single-build (dev) mode. */}
              <Route
                path="*"
                element={
                  IS_MARKETING ? (
                    <RedirectToApp />
                  ) : IS_APP ? (
                    <RedirectToMarketing />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
            </Routes>
            </ErrorBoundary>
          </Suspense>
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{ style: { zIndex: 99999 } }}
          />
        </div>
      </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
